import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { verifyOTP } from '../services/UserService';

interface OTPVerificationModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
  value: string;
  type: 'email' | 'mobile';
}

const OTPVerificationModal: React.FC<OTPVerificationModalProps> = ({
  show,
  onHide,
  onSuccess,
  value,
  type
}) => {
  const [otp, setOtp] = useState('');
  const [timeLeft, setTimeLeft] = useState(240); // 4 minutes in seconds
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (show) {
      setTimeLeft(240);
      setOtp('');
      setError(null);
    }
  }, [show]);

  useEffect(() => {
    if (timeLeft > 0 && show) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [timeLeft, show]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleVerify = async () => {
    if (!otp || otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await verifyOTP(value, otp);
      onSuccess();
      onHide();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const canRetry = type === 'email' && timeLeft === 0;

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Verify {type === 'email' ? 'Email' : 'Mobile'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="text-center mb-3">
          <p>Enter the 6-digit OTP sent to:</p>
          <strong>{value}</strong>
        </div>
        
        <Form.Group className="mb-3">
          <Form.Label>OTP Code</Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter 6-digit OTP"
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
            maxLength={6}
            className="text-center fs-4"
            style={{ letterSpacing: '0.5em' }}
          />
        </Form.Group>

        <div className="text-center mb-3">
          <div className={`fw-bold ${timeLeft <= 60 ? 'text-danger' : 'text-primary'}`}>
            Time remaining: {formatTime(timeLeft)}
          </div>
          {timeLeft === 0 && (
            <small className="text-muted">
              {canRetry ? 'You can close this popup and retry verification' : 'Time expired. Please try again.'}
            </small>
          )}
        </div>

        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
        <Button 
          variant="primary" 
          onClick={handleVerify}
          disabled={loading || timeLeft === 0 || otp.length !== 6}
        >
          {loading ? 'Verifying...' : 'Verify'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default OTPVerificationModal;